import {Injectable} from '@angular/core';
import {RequestsWebsocketService} from '../../../app-common/services/server/requests-websocket.service';
import {AuthService} from '../../../auth/services/auth/auth.service';
import {AppService} from '../../../app-common/services/app/app.service';
import {AddPostRequest} from '../../data/requests/addPostRequest';
import {PostDto} from '../../data/postDto';
import {Post} from '../../data/post';
import {PleaseWaitService} from '../../../app-common/services/please-wait/please-wait.service';
import {LexoRank} from 'lexorank';
import {TreeNode} from 'primeng/api';
import {PostTree} from '../../data/postTree';
import {UserDto} from '../../../user/data/userDto';
import {EditPostRequest} from '../../data/requests/editPostRequest';
import {QueryCreator, QueryDTO, QuerySort} from '../../../query/data/queryCreator';
import {Query} from '../../../query/data/query';
import {QueryableProperty} from '../../../query/data/queryableProperty';
import {ContainsValuePropertyFilter} from '../../../query/data/containsValuePropertyFilter';
import {PostProperties} from '../../../query/data/specifics/postProperties';
import {Observable, Subject} from 'rxjs';
import {ApiService} from '../../../app-common/services/server/api.service';
import {GenericVersion} from '../../../audit/data/GenericVersion';
import {LogLevel} from '../../../logging/data/logLevel';
import {LogSourceType} from '../../../logging/data/logSourceType';
import {LogSource} from '../../../logging/data/logSource';
import {LoggerService} from '../../../logging/services/logger.service';

@Injectable({
  providedIn: 'root'
})
export class PostService {

  private refreshDocListFlag = new Subject<void>();

  constructor(public websocket: RequestsWebsocketService,
              public authService: AuthService,
              public httpService: ApiService,
              public pleaseWaitService: PleaseWaitService,
              public appService: AppService,
              public loggerService: LoggerService) {
    this.websocket = websocket;
    this.authService = authService;
    this.appService = appService;
  }

  triggerRefreshDocsList(): void {
    this.refreshDocListFlag.next();
  }

  listenRefreshDocsList(): Observable<void> {
    return this.refreshDocListFlag.asObservable();
  }

  getDummyPostDTO() {
    const dto = new PostDto();
    const post = new Post();
    post.html = '<p></p>';
    post.title = '';
    post.parentId = 0;
    post.lexoRank = LexoRank.middle().toString();
    post.version = 1;
    dto.post = post;
    dto.tags = [];
    dto.author = new UserDto();
    dto.categories = [];
    return dto;
  }

  async addPost(postDto: PostDto) {
    this.pleaseWaitService.addToQueue();
    const request = new AddPostRequest();
    request.postDto = postDto;

    if (request.postDto.parent) {
      request.postDto.post.parentId = request.postDto.parent.post.id;
    }

    let response;
    try {
      response = await this.httpService.applicationApiClient.post('DocPost', request);
      this.appService.showSuccessMessage('Page Added', {windowId: 'main'});
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async editPost(postDto: PostDto, force: boolean = false) {
    const request = new EditPostRequest();
    this.pleaseWaitService.addToQueue();

    request.postDto = postDto;
    request.force = force;
    if (request.postDto.parent) {
      request.postDto.post.parentId = postDto.parent.post.id;
    } else {
      request.postDto.parent = null;
      request.postDto.post.parentId = 0;
    }

    let response;
    try {
      response = await this.httpService.applicationApiClient.put('DocPost', postDto.post.id, request);
      this.appService.showSuccessMessage('Page Edited', {windowId: 'main'});
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }

  async deletePost(postId: number) {
    this.pleaseWaitService.addToQueue();
    let response;
    try {
      response = await this.httpService.applicationApiClient.delete('DocPost', postId);
      this.appService.showSuccessMessage('Page Deleted');
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }


  async getPostsTree() {
    const response = await this.httpService.applicationApiClient.get<PostTree[]>('DocPost/Tree');
    return response;
  }

  convertPostsTreeToPrimeNodeTree(postTree: PostTree) {
    const root: TreeNode = {};

    root.key = postTree.post.post.id.toString();
    root.label = postTree.post.post.title;
    root.data = postTree.post;
    root.selectable = true;
    root.expanded = true;
    if (postTree.children) {
      root.children = [];
      for (const child of postTree.children) {
        root.children.push(this.convertPostsTreeToPrimeNodeTree(child));
      }
    }
    return root;
  }

  convertPostToPrimeTreeNode(post: PostDto) {
    const root: TreeNode = {};

    root.key = post.post.id.toString();
    root.label = post.post.title;
    if (post.parent !== undefined && post.parent !== null) {
      root.parent = this.convertPostToPrimeTreeNode(post.parent);
    }
    root.data = post.post;
    root.selectable = true;
    root.expanded = true;
    return root;
  }

  async getPostById(id: number, force: boolean = false) {
    const response = await this.httpService.applicationApiClient.getWithParams<PostDto>(`DocPost/${id}`, {force});
    return response;
  }

  async searchPost(query: string, entriesPerPage: number, pageNumber: number) {

    const finalQueryDto = this.buildSearchQueryModel(query, entriesPerPage, pageNumber);
    const response = await this.httpService.applicationApiClient.getWithParams<PostDto[]>(`DocPost/Search`, {filter: JSON.stringify(finalQueryDto)});
    return response;
  }

  buildSearchQueryModel(query: string, entriesPerPage: number, pageNumber: number): QueryDTO {
    const customerProperties = new PostProperties();
    const rootQuery = new Query();

    for (const property of customerProperties.properties) {
      if (property.name === 'title') {
        rootQuery.filters.push(this.createContainsFilter(property, query));
      }
    }
    const sorting = new QuerySort();
    sorting.dir = customerProperties.sortingDescending ? 'desc' : 'asc';
    sorting.field = customerProperties.sortingPropertyName;

    const queryDTO = QueryCreator.generateFilter(rootQuery, sorting, entriesPerPage, pageNumber, 'or');
    return queryDTO;
  }

  private createContainsFilter(property: QueryableProperty, toSearch: any) {
    const result = new ContainsValuePropertyFilter();
    result.active = true;
    result.property = property;
    result.queryValue = toSearch;
    return result;
  }

  async getPostHistory(postId: number, force: boolean = false): Promise<GenericVersion[]> {
    if (postId <= 0) {
      this.loggerService.logMessage(LogLevel.ERROR, LogSourceType.SERVICE, LogSource.PostService, 'Failed to fetch post audit!');
      return undefined;
    }
    return await this.httpService.applicationApiClient.getWithParams<GenericVersion[]>(`DocPost/History/${postId}`, {force});
  }

  async getPostVersion(postId: number, version: number, force: boolean = false): Promise<PostDto> {
    if (postId <= 0 || version <= 0) {
      return undefined;
    }
    return await this.httpService.applicationApiClient.getWithParams<PostDto>(`DocPost/History/${postId}/${version}`, null);
  }

  async getPostVersionLabels(postId: number, version: number, force: boolean = false): Promise<string[]> {
    if (postId <= 0 || version <= 0) {
      return undefined;
    }
    try {
      return await this.httpService.applicationApiClient.getWithParams<string[]>(`DocPost/History/${postId}/${version}/Labels`, null);
    } catch (e) {
      return ['INITIAL'];
    }
  }

  async revertPost(postDto: PostDto, version: number): Promise<boolean> {
    this.pleaseWaitService.addToQueue();
    const postId = postDto.post.id;

    let response;
    try {
      response = await this.httpService.applicationApiClient.get(`DocPost/History/${postId}/${version}/Revert`);
      this.appService.showSuccessMessage('Page Edited', {windowId: 'main'});
      this.loggerService.logMessage(LogLevel.INFO, LogSourceType.SERVICE, LogSource.PostService, 'Reverted post with the ID:' + postId);
      this.pleaseWaitService.removeFromQueue();
      return true;
    } catch (ex) {
      this.pleaseWaitService.removeFromQueue();
      return false;
    }
  }
}
